export const DRAW_LIBRARY_CATEGORY_ALL = "all";
export const DRAW_LIBRARY_CATEGORY_AUTO = "automatic";

export const DRAW_LIBRARY_CATEGORIES = [
  "Processos e Fluxos",
  "Funis / CRM / Vendas",
  "Negócios / Gestão",
  "Apresentações / Storytelling",
  "Produto / UX / UI",
  "Tecnologia / Arquitetura",
  "Cloud / DevOps / Dados",
  "Dashboards / Analytics",
  "Ícones Gerais",
  "Setas / Anotações / Destaques",
  "Educação / Workshops",
  "Mapas / Industrial",
  "Diversos",
] as const;

export const DRAW_LIBRARY_MANUAL_CATEGORY_OPTIONS = [
  { value: DRAW_LIBRARY_CATEGORY_ALL, label: "Todas" },
  ...DRAW_LIBRARY_CATEGORIES.map((category) => ({
    value: category,
    label: category,
  })),
];

export const DRAW_LIBRARY_AI_CATEGORY_OPTIONS = [
  { value: DRAW_LIBRARY_CATEGORY_AUTO, label: "Automático" },
  ...DRAW_LIBRARY_CATEGORIES.map((category) => ({
    value: category,
    label: category,
  })),
];

export type DrawLibraryCategoryOption = {
  value: string;
  label: string;
};

export type DrawLibraryCatalogStatus =
  | "idle"
  | "loading"
  | "ready"
  | "error";

export type DrawLibraryCatalogItem = {
  id: string;
  name: string;
  category: string;
  description: string;
  tags: string[];
  file: string;
  sourceType: "excalidrawlib";
};

export type DrawLibraryLightCatalogItem = Pick<
  DrawLibraryCatalogItem,
  "id" | "name" | "category" | "description" | "tags"
>;

export type DrawLibraryInsertMode = "insert" | "append-below" | "replace";

export type DrawLibraryFilePayload = {
  rawLibraryItems: unknown[];
  files?: Record<string, unknown>;
};

export type DrawLibraryErrorReason =
  | "catalog_missing"
  | "catalog_invalid"
  | "library_fetch_failed"
  | "library_invalid";

export class DrawLibraryError extends Error {
  reason: DrawLibraryErrorReason;

  constructor(reason: DrawLibraryErrorReason, message: string) {
    super(message);
    this.name = "DrawLibraryError";
    this.reason = reason;
  }
}

const CATALOG_URL = "/draw-libraries/catalog.json";
const LIGHT_CATALOG_LIMIT = 40;

const normalizeText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const tokenize = (value: string) =>
  normalizeText(value)
    .split(/[^a-z0-9]+/g)
    .filter((token) => token.length > 1);

const isCatalogItem = (item: unknown): item is DrawLibraryCatalogItem => {
  if (!item || typeof item !== "object") {
    return false;
  }

  const candidate = item as DrawLibraryCatalogItem;

  return (
    typeof candidate.id === "string" &&
    typeof candidate.name === "string" &&
    typeof candidate.category === "string" &&
    typeof candidate.description === "string" &&
    Array.isArray(candidate.tags) &&
    candidate.tags.every((tag) => typeof tag === "string") &&
    typeof candidate.file === "string" &&
    candidate.sourceType === "excalidrawlib"
  );
};

const toPublicUrl = (file: string) => {
  const normalizedFile = file.startsWith("/") ? file : `/${file}`;

  return encodeURI(normalizedFile);
};

const parseCatalog = (value: unknown) => {
  const items = Array.isArray(value)
    ? value
    : value && typeof value === "object" && Array.isArray((value as any).items)
      ? (value as any).items
      : null;

  if (!items || !items.every(isCatalogItem)) {
    throw new DrawLibraryError(
      "catalog_invalid",
      "Catalogo da Biblioteca Draw invalido.",
    );
  }

  return items;
};

export const loadDrawLibraryCatalog = async () => {
  let response: Response;

  try {
    response = await fetch(CATALOG_URL, {
      headers: { Accept: "application/json" },
    });
  } catch (error) {
    throw new DrawLibraryError(
      "catalog_missing",
      "Nao foi possivel carregar o catalogo da Biblioteca Draw.",
    );
  }

  if (!response.ok) {
    throw new DrawLibraryError(
      "catalog_missing",
      "Catalogo da Biblioteca Draw nao encontrado.",
    );
  }

  return parseCatalog(await response.json());
};

export const getDrawLibraryFileItems = (data: unknown) => {
  if (!data || typeof data !== "object") {
    throw new DrawLibraryError(
      "library_invalid",
      "Arquivo da Biblioteca Draw invalido.",
    );
  }

  const candidate = data as {
    library?: unknown;
    libraryItems?: unknown;
  };

  if (Array.isArray(candidate.libraryItems)) {
    return candidate.libraryItems;
  }

  if (Array.isArray(candidate.library)) {
    return candidate.library;
  }

  throw new DrawLibraryError(
    "library_invalid",
    "Arquivo sem library ou libraryItems.",
  );
};

export const loadDrawLibraryFile = async (
  item: DrawLibraryCatalogItem,
): Promise<DrawLibraryFilePayload> => {
  let response: Response;

  try {
    response = await fetch(toPublicUrl(item.file), {
      headers: { Accept: "application/json" },
    });
  } catch (error) {
    throw new DrawLibraryError(
      "library_fetch_failed",
      "Nao foi possivel carregar esta biblioteca.",
    );
  }

  if (!response.ok) {
    throw new DrawLibraryError(
      "library_fetch_failed",
      "Arquivo da Biblioteca Draw nao encontrado.",
    );
  }

  const data = await response.json();
  const rawLibraryItems = getDrawLibraryFileItems(data);
  const files =
    data &&
    typeof data === "object" &&
    (data as { files?: unknown }).files &&
    typeof (data as { files?: unknown }).files === "object" &&
    !Array.isArray((data as { files?: unknown }).files)
      ? ((data as { files: Record<string, unknown> }).files)
      : undefined;

  return { rawLibraryItems, files };
};

export const filterDrawLibraryCatalog = ({
  catalog,
  category,
  query,
}: {
  catalog: DrawLibraryCatalogItem[];
  category: string;
  query: string;
}) => {
  const normalizedQuery = normalizeText(query.trim());

  return catalog.filter((item) => {
    const matchesCategory =
      !category ||
      category === DRAW_LIBRARY_CATEGORY_ALL ||
      category === DRAW_LIBRARY_CATEGORY_AUTO ||
      item.category === category;

    if (!matchesCategory) {
      return false;
    }

    if (!normalizedQuery) {
      return true;
    }

    const searchable = normalizeText(
      `${item.name} ${item.category} ${item.description} ${item.tags.join(
        " ",
      )}`,
    );

    return searchable.includes(normalizedQuery);
  });
};

const scoreCatalogItem = ({
  item,
  queryTokens,
}: {
  item: DrawLibraryCatalogItem;
  queryTokens: string[];
}) => {
  if (!queryTokens.length) {
    return 0;
  }

  const name = normalizeText(item.name);
  const category = normalizeText(item.category);
  const tags = normalizeText(item.tags.join(" "));
  const description = normalizeText(item.description);

  return queryTokens.reduce((score, token) => {
    if (name.includes(token)) {
      return score + 5;
    }

    if (tags.includes(token)) {
      return score + 3;
    }

    if (category.includes(token)) {
      return score + 2;
    }

    if (description.includes(token)) {
      return score + 1;
    }

    return score;
  }, 0);
};

export const buildLightweightDrawLibraryCatalog = ({
  catalog,
  category,
  query,
}: {
  catalog: DrawLibraryCatalogItem[];
  category: string;
  query: string;
}): DrawLibraryLightCatalogItem[] => {
  const filteredCatalog = filterDrawLibraryCatalog({
    catalog,
    category,
    query: "",
  });
  const queryTokens = tokenize(query);

  return filteredCatalog
    .map((item) => ({
      item,
      score: scoreCatalogItem({ item, queryTokens }),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }

      return a.item.name.localeCompare(b.item.name);
    })
    .slice(0, LIGHT_CATALOG_LIMIT)
    .map(({ item }) => ({
      id: item.id,
      name: item.name,
      category: item.category,
      description: item.description,
      tags: item.tags,
    }));
};

export const getDrawLibraryErrorMessage = (error: unknown) => {
  if (error instanceof DrawLibraryError) {
    return error.message;
  }

  return "Nao foi possivel carregar a Biblioteca Draw.";
};
